import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../../../components/Layout';
import { menuTypeService } from '../../../../services/menuTypeService';
import { CreateMenuTypeDTO } from '../../../../types';
import { ArrowLeft, UtensilsCrossed, Smile, Hash, AlertCircle, Zap } from 'lucide-react';
import '../RestaurantPage.css';

import { getServerErrorMessage } from '../../../../utils/errorUtils';
interface FormData {
  name: string;
  description: string;
  iconName: string;
  sortOrder: number;
  isActive: boolean;
}

const MenuTypeCreatePage: React.FC = () => {
  const navigate    = useNavigate();
  const { id }      = useParams<{ id: string }>();
  const { t }       = useTranslation();
  const isEdit      = Boolean(id);

  const [saving,      setSaving]      = useState(false);
  const [loadingData, setLoadingData] = useState(isEdit);
  const [error,       setError]       = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [formData,    setFormData]    = useState<FormData>({
    name: '', description: '', iconName: '', sortOrder: 0, isActive: true,
  });

  useEffect(() => {
    if (isEdit && id) {
      setLoadingData(true);
      menuTypeService.getById(parseInt(id))
        .then(res => {
          const d = (res.data as any)?.data ?? res.data;
          setFormData({
            name:        d.name        ?? '',
            description: d.description ?? '',
            iconName:    d.iconName    ?? '',
            sortOrder:   d.sortOrder   ?? 0,
            isActive:    d.isActive    ?? true,
          });
        })
        .catch(() => setError(t('restaurant.menuType.failedToLoad')))
        .finally(() => setLoadingData(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const set = (field: keyof FormData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { value, type } = e.target;
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(p => ({ ...p, [field]: type === 'checkbox' ? checked : type === 'number' ? Number(value) : value }));
      setFieldErrors(p => ({ ...p, [field]: undefined }));
    };

  const validate = () => {
    const errs: Partial<Record<keyof FormData, string>> = {};
    if (!formData.name.trim()) errs.name = t('common.required');
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    setError(null);
    const payload: CreateMenuTypeDTO = {
      name:        formData.name.trim(),
      description: formData.description.trim() || undefined,
      iconName:    formData.iconName.trim()    || undefined,
      sortOrder:   formData.sortOrder,
      isActive:    formData.isActive,
    };
    try {
      if (isEdit && id) await menuTypeService.update(parseInt(id), payload);
      else              await menuTypeService.create(payload);
      navigate('/restaurant/menu-types');
    } catch (err: any) {
      setError(getServerErrorMessage(err) ?? t('restaurant.menuType.failedToSave'));
      setSaving(false);
    }
  };

  if (loadingData) {
    return (
      <Layout>
        <div className="ims-create-body-page rest-create-page" style={{ alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
          <div className="spinner-border" role="status"><span className="visually-hidden">{t('common.loading')}</span></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <form onSubmit={handleSubmit} className="rest-create-page">
        <div className="ims-create-body-page">

          {/* Top bar */}
          <div className="ims-top-bar">
            <div className="ims-top-bar-left">
              <button type="button" className="ims-back-btn" onClick={() => navigate('/restaurant/menu-types')}>
                <ArrowLeft size={13} />
                {t('restaurant.menuType.title')}
              </button>
              <div>
                <h1 className="ims-page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <UtensilsCrossed size={18} style={{ color: 'var(--ims-accent)' }} />
                  {isEdit ? t('restaurant.menuType.editTitle') : t('restaurant.menuType.createTitle')}
                </h1>
                <p className="ims-page-subtitle">
                  {isEdit ? 'Update the menu type details below' : 'Define a new menu type for grouping your menu categories'}
                </p>
              </div>
            </div>
            <div className="ims-top-bar-right">
              <button type="button" className="ims-btn ims-btn-secondary" onClick={() => navigate('/restaurant/menu-types')} disabled={saving}>
                {t('common.cancel')}
              </button>
              <button type="submit" className="ims-btn ims-btn-primary" disabled={saving}>
                {saving
                  ? <><span className="spinner-border spinner-border-sm me-1" role="status" />{t('common.saving')}</>
                  : (isEdit ? t('common.update') : t('common.save'))}
              </button>
            </div>
          </div>

          {/* Error banner */}
          {error && (
            <div className="ims-error-banner">
              <div className="ims-alert ims-alert-error" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <AlertCircle size={15} />
                <span style={{ flex: 1 }}>{error}</span>
                <button type="button" className="btn-close" onClick={() => setError(null)} />
              </div>
            </div>
          )}

          {/* Body — single centred column for simple forms */}
          <div className="ims-create-body ims-create-body-single">
            <div className="ims-panel-center" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

              {/* Details card */}
              <div className="ims-card mi-card">
                <div className="ims-card-header mi-card-header">
                  <UtensilsCrossed size={14} className="mi-header-icon" />
                  <span className="ims-card-title">{t('restaurant.menuType.details', 'Type Details')}</span>
                </div>
                <div className="ims-card-body mi-card-body">

                  {/* Name */}
                  <div className={`mi-field${fieldErrors.name ? ' mi-field-error' : ''}`}>
                    <label className="mi-label">
                      {t('restaurant.menuType.name')} <span className="mi-required">*</span>
                    </label>
                    <input
                      type="text"
                      className="mi-input"
                      value={formData.name}
                      onChange={set('name')}
                      placeholder={t('restaurant.menuType.namePlaceholder', 'e.g. Food, Beverages, Desserts')}
                      maxLength={100}
                      autoFocus
                    />
                    {fieldErrors.name && <span className="mi-err">{fieldErrors.name}</span>}
                  </div>

                  {/* Description */}
                  <div className="mi-field">
                    <label className="mi-label">{t('common.description')}</label>
                    <textarea
                      className="mi-input mi-textarea"
                      value={formData.description}
                      onChange={set('description')}
                      rows={3}
                      placeholder={t('restaurant.menuType.descriptionPlaceholder', 'Brief description of this menu type')}
                      maxLength={500}
                    />
                  </div>

                  {/* Icon + Sort Order row */}
                  <div className="mi-row-2">
                    <div className="mi-field">
                      <label className="mi-label">
                        <Smile size={11} className="mi-label-icon" />
                        {t('restaurant.menuType.iconName', 'Icon Name')}
                      </label>
                      <input
                        type="text"
                        className="mi-input mi-input-mono"
                        value={formData.iconName}
                        onChange={set('iconName')}
                        placeholder={t('restaurant.menuType.iconNamePlaceholder', 'e.g. utensils')}
                        maxLength={100}
                      />
                    </div>
                    <div className="mi-field">
                      <label className="mi-label">
                        <Hash size={11} className="mi-label-icon" />
                        {t('restaurant.menuType.sortOrder', 'Sort Order')}
                      </label>
                      <input
                        type="number"
                        className="mi-input"
                        value={formData.sortOrder}
                        onChange={set('sortOrder')}
                        min={0}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Status card */}
              <div className="ims-card mi-card">
                <div className="ims-card-header mi-card-header">
                  <Zap size={14} className="mi-header-icon" />
                  <span className="ims-card-title">{t('common.status')}</span>
                </div>
                <div className="ims-card-body mi-card-body">
                  <label className={`mi-toggle-row${formData.isActive ? ' mi-toggle-on' : ''}`}>
                    <div className="mi-toggle-text">
                      <span className="mi-toggle-label">{t('common.active')}</span>
                      <span className="mi-toggle-sub">Make this type visible and selectable across the system</span>
                    </div>
                    <div className={`mi-switch${formData.isActive ? ' mi-switch-on' : ''}`}
                      onClick={() => setFormData(p => ({ ...p, isActive: !p.isActive }))}>
                      <div className="mi-switch-thumb" />
                    </div>
                  </label>
                </div>
              </div>

            </div>
          </div>
        </div>
      </form>
    </Layout>
  );
};

export default MenuTypeCreatePage;
